import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Container,
  Card,
  CardContent,
  Paper,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  School as SchoolIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { markLeaderboardPopupForLogin } from '../../components/Students/LmsLeaderboardLoginPopup';
import Msg91OtpWidget, { isMsg91WidgetConfigured } from '../../components/Msg91OtpWidget';
import {
  DEV_RUN_OTP_DELIVERY_PHONE,
  isDevRunLoginPhone,
} from '../../utils/studentDevAccess';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();
const STUDENT_DASHBOARD_BASE_URL = process.env.REACT_APP_PRODUCTION_URL || 'https://services.knls.in';

const StudentLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('login');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [formData, setFormData] = useState({
    phone: '',
    password: '',
  });
  const [registerForm, setRegisterForm] = useState({
    name: '',
    phone: '',
    date_of_birth: '',
    qualification: '',
    password: '',
    confirmPassword: '',
    otp: '',
  });
  const [registerTermsAccepted, setRegisterTermsAccepted] = useState(false);
  const [msg91AccessToken, setMsg91AccessToken] = useState('');
  const [msg91RequestUserId, setMsg91RequestUserId] = useState('');
  const [devPhoneVerified, setDevPhoneVerified] = useState(false);
  const msg91Ready = isMsg91WidgetConfigured();
  const isDevRunLogin = isDevRunLoginPhone(formData.phone);

  // Auto-login if URL parameters are provided
  useEffect(() => {
    const phoneParam = searchParams.get('phone');
    const passwordParam = searchParams.get('password');
    const autoParam = searchParams.get('auto');

    if (autoParam === 'true' && phoneParam && passwordParam) {
      // Set form data
      setFormData({
        phone: phoneParam.replace(/\D/g, ''),
        password: passwordParam,
      });
      
      // Auto-submit after a short delay
      setTimeout(() => {
        handleAutoLogin(phoneParam.replace(/\D/g, ''), passwordParam);
      }, 500);
    } else if (phoneParam) {
      // Just populate phone if provided
      setFormData(prev => ({
        ...prev,
        phone: phoneParam.replace(/\D/g, ''),
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (otpCooldown <= 0) {
      return undefined;
    }

    const timer = setInterval(() => {
      setOtpCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [otpCooldown]);

  const resetDevMsg91State = () => {
    setMsg91AccessToken('');
    setMsg91RequestUserId('');
    setDevPhoneVerified(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const resolvePostLoginPath = () => {
    const redirectParam = searchParams.get('redirect');
    if (
      redirectParam &&
      redirectParam.startsWith('/student/') &&
      !redirectParam.startsWith('//')
    ) {
      return redirectParam;
    }
    return '/student/dashboard';
  };

  const performLogin = async (phone, password) => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/students/login`, {
        phone: phone.replace(/\D/g, ''),
        password: password,
      });

      if (response.data.success) {
        // Store student token
        localStorage.setItem('student_token', response.data.token);
        localStorage.setItem('student_data', JSON.stringify(response.data.student));
        markLeaderboardPopupForLogin();
        
        toast.success('Login successful!');
        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1';
        const targetPath = resolvePostLoginPath();

        if (isLocalDev) {
          navigate(targetPath);
        } else if (typeof window !== 'undefined') {
          window.location.assign(`${STUDENT_DASHBOARD_BASE_URL}${targetPath}`);
        }
        return true;
      } else {
        setError(response.data.message || 'Login failed');
        toast.error(response.data.message || 'Login failed');
        return false;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'An error occurred. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAutoLogin = async (phone, password) => {
    await performLogin(phone, password);
  };

  const completeDevRunMsg91Login = async (accessToken, meta = {}) => {
    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (!isDevRunLoginPhone(cleanPhone)) {
      setError('MSG91 login is only available for the dev-run student number');
      return false;
    }
    if (!accessToken) {
      setError('Complete MSG91 phone verification first.');
      return false;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/students/login/verify-msg91`, {
        phone: cleanPhone,
        msg91_access_token: accessToken,
        msg91_request_user_id: String(meta.requestUserId || meta.reqId || msg91RequestUserId || '').trim(),
      });

      if (response.data?.success) {
        localStorage.setItem('student_token', response.data.token);
        localStorage.setItem('student_data', JSON.stringify(response.data.student));
        markLeaderboardPopupForLogin();
        toast.success('Login successful!');

        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1';
        const targetPath = resolvePostLoginPath();

        if (isLocalDev) {
          navigate(targetPath);
        } else if (typeof window !== 'undefined') {
          window.location.assign(`${STUDENT_DASHBOARD_BASE_URL}${targetPath}`);
        }
        return true;
      }

      setError(response.data?.message || 'Login failed');
      toast.error(response.data?.message || 'Login failed');
      return false;
    } catch (err) {
      const message = err.response?.data?.message || 'MSG91 login failed';
      setError(message);
      toast.error(message);
      resetDevMsg91State();
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDevRunLogin) {
      // Dev-run: verify OTP button already signs in and opens the dashboard.
      if (devPhoneVerified && msg91AccessToken && !loading) {
        await completeDevRunMsg91Login(msg91AccessToken, { requestUserId: msg91RequestUserId });
      }
      return;
    }
    await performLogin(formData.phone, formData.password);
  };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const sendRegistrationOtp = async () => {
    const cleanPhone = registerForm.phone.replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      setError('Enter a valid 10-digit mobile number');
      return;
    }

    try {
      setSendingOtp(true);
      setError('');
      const response = await axios.post(`${API_BASE_URL}/students/registration/send-otp`, {
        phone: cleanPhone,
      });

      if (response.data?.success) {
        setOtpSent(true);
        setOtpCooldown(30);
        toast.success('OTP sent successfully');
        if (response.data?.otp) {
          setRegisterForm((prev) => ({ ...prev, otp: String(response.data.otp) }));
          toast.info(`Test OTP: ${response.data.otp}`);
        }
      } else {
        setError(response.data?.message || 'Failed to send OTP');
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to send OTP';
      setError(message);
      toast.error(message);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();

    if (!registerForm.name.trim()) {
      setError('Name is required');
      return;
    }

    if (registerForm.phone.replace(/\D/g, '').length !== 10) {
      setError('Enter a valid 10-digit mobile number');
      return;
    }

    if (!registerForm.password || registerForm.password.length < 4) {
      setError('Password must be at least 4 characters');
      return;
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      setError('Password and confirm password do not match');
      return;
    }

    if (!registerForm.otp || registerForm.otp.length !== 6) {
      setError('Enter a valid 6-digit OTP');
      return;
    }

    if (!registerTermsAccepted) {
      setError('You must accept Terms & Conditions and the non-refundable fee policy');
      return;
    }

    try {
      setRegistering(true);
      setError('');

      const response = await axios.post(`${API_BASE_URL}/students/registration/verify-otp`, {
        name: registerForm.name.trim(),
        phone: registerForm.phone.replace(/\D/g, ''),
        otp: registerForm.otp,
        password: registerForm.password,
        date_of_birth: registerForm.date_of_birth || null,
        qualification: registerForm.qualification || null,
        accepted_terms: true,
      });

      if (response.data?.success) {
        toast.success('Registration successful. Please login.');
        setFormData({
          phone: registerForm.phone.replace(/\D/g, ''),
          password: registerForm.password,
        });
        setMode('login');
        setOtpSent(false);
        setOtpCooldown(0);
        setRegisterForm({
          name: '',
          phone: '',
          date_of_birth: '',
          qualification: '',
          password: '',
          confirmPassword: '',
          otp: '',
        });
        setRegisterTermsAccepted(false);
      } else {
        setError(response.data?.message || 'Registration failed');
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Registration failed';
      setError(message);
      toast.error(message);
    } finally {
      setRegistering(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card
          elevation={24}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: 3,
              textAlign: 'center',
            }}
          >
            <SchoolIcon sx={{ fontSize: 60, mb: 1 }} />
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              {mode === 'login' ? 'Student Login' : 'Student Registration'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {mode === 'login'
                ? 'Enter your credentials to access your dashboard'
                : 'Register with OTP using the same Citizen/Mitra OTP flow'}
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button
                fullWidth
                variant={mode === 'login' ? 'contained' : 'outlined'}
                onClick={() => {
                  setMode('login');
                  setError('');
                }}
              >
                Login
              </Button>
              <Button
                fullWidth
                variant={mode === 'register' ? 'contained' : 'outlined'}
                startIcon={<PersonAddIcon />}
                onClick={() => {
                  setMode('register');
                  setError('');
                }}
              >
                Register
              </Button>
            </Stack>

            {mode === 'login' ? (
            <Box component="form" onSubmit={handleSubmit}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Mobile Number"
                name="phone"
                value={formData.phone}
                onChange={(e) => {
                  handleChange(e);
                  if (!isDevRunLoginPhone(e.target.value)) {
                    resetDevMsg91State();
                  }
                }}
                required
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder="9885750476"
                inputProps={{
                  maxLength: 10,
                  pattern: '[0-9]*',
                }}
                helperText={
                  isDevRunLogin
                    ? `Dev-run login: OTP will be sent to ${DEV_RUN_OTP_DELIVERY_PHONE}`
                    : 'Enter your 10-digit mobile number'
                }
              />

              {isDevRunLogin ? (
                <Stack spacing={1.5} sx={{ mt: 2, width: '100%' }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      Phone verification (MSG91)
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    OTP is sent by MSG91 directly to <strong>{DEV_RUN_OTP_DELIVERY_PHONE}</strong> (not via our server SMS).
                    Tap <strong>Send OTP</strong> → enter the SMS code → <strong>Verify OTP &amp; Continue</strong>.
                  </Typography>
                  {!msg91Ready ? (
                    <Alert severity="warning">
                      MSG91 widget is not configured. Use the same REACT_APP_MSG91_OTP_WIDGET_ID and
                      REACT_APP_MSG91_OTP_WIDGET_TOKEN_AUTH as locsydashboard/register.
                    </Alert>
                  ) : (
                    <Msg91OtpWidget
                      phoneDigits10={DEV_RUN_OTP_DELIVERY_PHONE}
                      disabled={loading}
                      verifyButtonLabel="Verify OTP & Continue to Dashboard"
                      onVerified={(token, meta) => {
                        const sessionId = meta && String(meta.requestUserId || meta.reqId || '').trim();
                        setMsg91AccessToken(token);
                        setMsg91RequestUserId(sessionId || '');
                        setDevPhoneVerified(true);
                        setError('');
                        completeDevRunMsg91Login(token, { requestUserId: sessionId || '' });
                      }}
                      onError={(msg) => {
                        resetDevMsg91State();
                        setError(msg || 'MSG91 verification failed');
                      }}
                    />
                  )}
                  {loading && (
                    <Alert severity="info" icon={<CircularProgress size={18} />}>
                      Signing you in…
                    </Alert>
                  )}
                </Stack>
              ) : (
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                placeholder="Last 4 digits + Year of birth"
                helperText="Format: Last 4 digits of phone + Year of birth (e.g., 04761977)"
              />
              )}

              {!isDevRunLogin && (
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
              </Button>
              )}

              {!isDevRunLogin && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mt: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Password Format:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last 4 digits of your mobile number + Year of birth
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Example:</strong> If your mobile is 9885750476 and DOB is 02/03/1977,
                  <br />
                  Password: <strong>04761977</strong>
                </Typography>
              </Paper>
              )}
            </Box>
            ) : (
              <Box component="form" onSubmit={handleRegisterSubmit}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {otpSent && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    OTP sent. Enter the 6-digit OTP to complete registration.
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={registerForm.name}
                  onChange={handleRegisterChange}
                  required
                  margin="normal"
                />

                <TextField
                  fullWidth
                  label="Mobile Number"
                  name="phone"
                  value={registerForm.phone}
                  onChange={handleRegisterChange}
                  required
                  margin="normal"
                  inputProps={{ maxLength: 10 }}
                />

                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="date_of_birth"
                  type="date"
                  value={registerForm.date_of_birth}
                  onChange={handleRegisterChange}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  fullWidth
                  label="Qualification"
                  name="qualification"
                  value={registerForm.qualification}
                  onChange={handleRegisterChange}
                  margin="normal"
                />

                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={registerForm.password}
                  onChange={handleRegisterChange}
                  required
                  margin="normal"
                  helperText="Minimum 4 characters"
                />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={registerForm.confirmPassword}
                  onChange={handleRegisterChange}
                  required
                  margin="normal"
                />

                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    label="OTP"
                    name="otp"
                    value={registerForm.otp}
                    onChange={handleRegisterChange}
                    required
                    inputProps={{ maxLength: 6 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={sendRegistrationOtp}
                    disabled={sendingOtp || otpCooldown > 0}
                    sx={{ minWidth: 130, mt: 1 }}
                  >
                    {sendingOtp
                      ? 'Sending...'
                      : otpSent
                      ? (otpCooldown > 0 ? `Resend (${otpCooldown}s)` : 'Resend OTP')
                      : 'Send OTP'}
                  </Button>
                </Stack>

                {otpSent && otpCooldown > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    You can resend OTP after {otpCooldown} seconds.
                  </Typography>
                )}

                <FormControlLabel
                  sx={{ alignItems: 'flex-start', mt: 2 }}
                  control={
                    <Checkbox
                      checked={registerTermsAccepted}
                      onChange={(e) => setRegisterTermsAccepted(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      I agree to Terms & Conditions and understand that fees are non-refundable.{' '}
                      <RouterLink to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                        Open Terms & Conditions
                      </RouterLink>
                    </Typography>
                  }
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={registering}
                  sx={{ mt: 3, py: 1.5 }}
                >
                  {registering ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP & Register'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        <Typography variant="caption" color="common.white" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
          <RouterLink to="/terms-and-conditions" style={{ color: 'inherit', textDecoration: 'underline' }}>
            Open Terms & Conditions
          </RouterLink>
        </Typography>
      </Container>
    </Box>
  );
};

export default StudentLogin;


